
import React, { useState, useEffect } from 'react';
import { ServiceItem, SERVICE_CATALOG, ServiceAssignment, ServiceTemplate } from '../../types';
import { supabase } from '../../services/supabaseClient';
import { Button } from '../Button';
import { Database, Plus, Trash2, ListChecks, Save, Check, Loader2, Search, LayoutTemplate, Edit3, ArrowLeft, CheckCircle2, ChevronDown } from 'lucide-react';
import { isMonthlyUnit } from '../../services/serviceUnits';
import { BestandteileEditor } from '../bestandteile/BestandteileEditor';
import { StundenKontingentFeld } from '../bestandteile/StundenKontingentFeld';
import { bestandteilePatch, mitVorlagenKopie, pruefPosition, pruefeBestandteilListe } from '../bestandteile/bestandteileEditorLogic';
import { formatStunden, hatBestandteile, kopiereVorlage, normalisiereBestandteile } from '../../services/bestandteile/bestandteilLogic';
import { gueltigesKontingent, kontingentFuerKopie } from '../../services/bestandteile/kontingentKopie';
import { katalogKontingentAusZeile, katalogKontingentFuerZeile } from '../../services/projectLv';
import type { PositionsBestandteil } from '../../types';
import {
    CATALOG_FLAT_SCOPES,
    FLAT_SCOPE_LABELS,
    FlatScopeChoice,
    applyFlatScopeChoice,
    flatPatchForAssignmentChange,
    flatPatchForUnitChange,
    flatScopeChoiceOf,
} from '../../services/flatRateSetup';

interface Props {
  onSaveStatusChange: (status: any) => void;
}

export const AdminCatalogEditor: React.FC<Props> = ({ onSaveStatusChange }) => {
    const [activeTab, setActiveTab] = useState<'master' | 'templates'>('master');
    
    // Master Catalog State
    const [customCatalog, setCustomCatalog] = useState<any[]>([]);
    const [savingRowId, setSavingRowId] = useState<string | null>(null);
    const [savedRowId, setSavedRowId] = useState<string | null>(null);
    const [newService, setNewService] = useState<Partial<ServiceItem>>({
      lvPosition: '', title: '', unit: 'Stück', category: 'Ausführung', unitPrice: 0, isDefault: false, assignment: 'none'
    });
    // PAKETPOSITION: Bestandteil-Vorlage je Katalogposition. Bearbeitet wird
    // ein ENTWURF der aufgeklappten Zeile; gespeichert erst per Knopf — der
    // Editor meldet jeden Tastendruck, und je Tastendruck ein Update auf
    // service_catalog waere eine Flut halber Labels in der Datenbank.
    const [vorlageOffenId, setVorlageOffenId] = useState<string | null>(null);
    const [vorlageEntwurf, setVorlageEntwurf] = useState<PositionsBestandteil[] | null>(null);
    const [vorlageFehlschlag, setVorlageFehlschlag] = useState(false);
    // STUNDENKONTINGENT der aufgeklappten Zeile (service_catalog.stunden_kontingent,
    // Nr. 171) — Entwurf wie die Vorlage, gespeichert mit ihr in EINEM Update.
    // Gibt es nur zusammen mit Bestandteilen (S1, docs/stundenkontingent-vertrag.md).
    const [kontingentEntwurf, setKontingentEntwurf] = useState<number | undefined>(undefined);
    const [kontingentEntwurfFehler, setKontingentEntwurfFehler] = useState<string | null>(null);
    const [neuVorlageAuf, setNeuVorlageAuf] = useState(false);
    // Ungueltige Kontingent-Eingabe an der NEUEN Position — sperrt „Add" wie
    // eine ungueltige Bestandteil-Liste.
    const [neuKontingentFehler, setNeuKontingentFehler] = useState<string | null>(null);
    // Ungueltige Vorlage beim Anlegen: nach dem ersten „Add" steht die
    // Meldung unter dem Bestandteile-Schalter — live aus newService, sie
    // verschwindet also, sobald die Liste passt.
    const [neuVorlageGeprueft, setNeuVorlageGeprueft] = useState(false);

    // Template State
    const [templates, setTemplates] = useState<ServiceTemplate[]>([]);
    const [isEditingTemplate, setIsEditingTemplate] = useState<string | null>(null); // Template ID or 'new'
    const [currentTemplate, setCurrentTemplate] = useState<Partial<ServiceTemplate>>({ name: '', items: [] });
    const [itemSearch, setItemSearch] = useState('');

    const sortCatalogItems = (items: any[]) => {
        return [...items].sort((a: any, b: any) => 
            (a.lvPosition || '').localeCompare(b.lvPosition || '', undefined, { numeric: true, sensitivity: 'base' })
        );
    };

    const fetchCatalog = async () => {
      const { data } = await supabase
          .from('service_catalog')
          .select('*')
          .order('lv_position', { ascending: true });
          
      if (data) {
          const mappedCatalog = data.map((item: any) => ({
              id: item.id,
              lvPosition: item.lv_position,
              title: item.title,
              unit: item.unit,
              unitPrice: item.unit_price,
              category: item.category,
              isDefault: item.is_default,
              assignment: item.assignment || 'none',
              flatRate: item.flat_rate ?? undefined,
              // Bestandteil-Vorlage (Spalte service_catalog.bestandteile,
              // SQL Nr. 165). Fehlt die Spalte, ist das Feld undefined und
              // der Katalog arbeitet still weiter (Muster flat_rate).
              bestandteile: normalisiereBestandteile(item.bestandteile) ?? undefined,
              // Vorgabe des Stundenkontingents (Spalte
              // service_catalog.stunden_kontingent, SQL Nr. 171) — intern,
              // nie auf Rechnung. Fehlt die Spalte oder ist sie leer: undefined.
              stundenKontingent: katalogKontingentAusZeile(item)
          }));
          setCustomCatalog(sortCatalogItems(mappedCatalog));
      }
    };

    const fetchTemplates = async () => {
        const { data } = await supabase.from('service_templates').select('*').order('created_at', { ascending: false });
        if (data) {
            setTemplates(data);
        }
    };
    
    useEffect(() => {
        fetchCatalog();
        fetchTemplates();
    }, []);

    // --- MASTER CATALOG FUNCTIONS ---
    const handleSeedCatalog = async () => {
      if (!confirm("Möchten Sie den Standard-Katalog in die Datenbank importieren? Dies sollte nur einmalig geschehen.")) return;
      onSaveStatusChange('saving');
      try {
          const payload = SERVICE_CATALOG.map(s => ({
              lv_position: s.lvPosition, title: s.title, unit: s.unit, unit_price: s.unitPrice, category: s.category, is_default: s.isDefault, assignment: s.assignment
          }));
          const { error } = await supabase.from('service_catalog').insert(payload);
          if (error) throw error;
          await fetchCatalog();
          alert("Katalog erfolgreich initialisiert!");
          onSaveStatusChange('saved');
      } catch (e: any) {
          alert("Fehler beim Initialisieren: " + e.message);
          onSaveStatusChange('error');
      } 
    };

    // Bestandteil-Vorlage: eine ungueltige Liste (leeres Label, auf einer
    // Monatspauschale) gar nicht erst anlegen.
    const neuKontingent = hatBestandteile(newService) ? gueltigesKontingent(newService.stundenKontingent) : undefined;
    const neuVorlagenFehler = hatBestandteile(newService)
        ? pruefeBestandteilListe(newService.bestandteile || [], pruefPosition({ unit: newService.unit, assignment: newService.assignment, flatRate: newService.flatRate }))
        : null;

    const handleAddService = async () => {
      if (!newService.title) return;
      if (neuVorlagenFehler || (hatBestandteile(newService) && neuKontingentFehler)) {
          // Meldung inline (unter dem Schalter), der Editor geht dazu auf.
          setNeuVorlageGeprueft(true);
          setNeuVorlageAuf(true);
          return;
      }
      onSaveStatusChange('saving');
      const dbPayload = {
          lv_position: newService.lvPosition, title: newService.title, unit: newService.unit, unit_price: newService.unitPrice, category: newService.category, is_default: newService.isDefault, assignment: newService.assignment,
          // Deckungsumfang der Monatspauschale — nur mitschicken, wenn gesetzt:
          // Positionen ohne Pauschale bleiben so auch dann anlegbar, wenn die
          // Spalte service_catalog.flat_rate (SQL Nr. 54) noch fehlt.
          ...(newService.flatRate ? { flat_rate: newService.flatRate } : {}),
          // Dasselbe fuer die Bestandteil-Vorlage (SQL Nr. 165).
          ...(hatBestandteile(newService) ? { bestandteile: newService.bestandteile } : {}),
          // ... und fuer das Stundenkontingent (SQL Nr. 171) — nur mit
          // Bestandteil-Vorlage (S1), nur ein gueltiger Wert.
          ...katalogKontingentFuerZeile(newService)
      };
      const { error } = await supabase.from('service_catalog').insert([dbPayload]);
      if (!error) {
          await fetchCatalog();
          setNewService({ lvPosition: '', title: '', unit: 'Stück', category: 'Ausführung', unitPrice: 0, isDefault: false, assignment: 'none' });
          setNeuVorlageAuf(false);
          setNeuVorlageGeprueft(false);
          setNeuKontingentFehler(null);
          onSaveStatusChange('saved');
      } else {
          alert("Fehler beim Hinzufügen: " + error.message);
          onSaveStatusChange('error'); 
      }
    };

    const handleRemoveService = async (index: number) => {
      const item = customCatalog[index];
      if (item && item.id) {
          if (!confirm("Position wirklich löschen?")) return;
          onSaveStatusChange('saving'); 
          const { error } = await supabase.from('service_catalog').delete().eq('id', item.id);
          if (!error) { await fetchCatalog(); onSaveStatusChange('saved'); } else { onSaveStatusChange('error'); }
      }
    };

    // Mehrere Felder in EINEM Update: die Monatspauschale aendert assignment
    // und flat_rate immer als Paar (flatRateSetup.FlatSetupPatch) — zwei
    // getrennte Requests koennten sich in der halb angelegten Pauschale
    // treffen, wenn der zweite fehlschlaegt.
    const handleUpdateServiceFields = async (index: number, fields: Partial<ServiceItem>): Promise<boolean> => {
      const item = customCatalog[index];
      if (item && item.id) {
          setSavingRowId(item.id);
          setSavedRowId(null);
          onSaveStatusChange('saving');

          const dbFieldMap: Record<string, string> = {
            lvPosition: 'lv_position',
            unitPrice: 'unit_price',
            isDefault: 'is_default',
            title: 'title',
            unit: 'unit',
            category: 'category',
            assignment: 'assignment',
            flatRate: 'flat_rate',
            bestandteile: 'bestandteile',
            // numeric-Spalte (Nr. 171): `undefined` geht unten als null
            // raus — „kein Kontingent" braucht das ausdrueckliche null.
            stundenKontingent: 'stunden_kontingent'
          };
          const dbPayload: Record<string, any> = {};
          Object.entries(fields).forEach(([field, value]) => {
              // jsonb-Spalte: undefined wuerde beim Serialisieren wegfallen und
              // die alte Deckung stehen lassen — die Ruecknahme braucht null.
              dbPayload[dbFieldMap[field] || field] = value === undefined ? null : value;
          });

          const { error } = await supabase
            .from('service_catalog')
            .update(dbPayload)
            .eq('id', item.id);

          if (!error) {
             const updated = [...customCatalog];
             updated[index] = { ...item, ...fields };
             setCustomCatalog(updated);
             setSavingRowId(null);
             setSavedRowId(item.id);
             onSaveStatusChange('saved');
             setTimeout(() => setSavedRowId(null), 2000);
             return true;
          } else {
             setSavingRowId(null);
             onSaveStatusChange('error');
             return false;
          }
      }
      return false;
    };

    // --- BESTANDTEIL-VORLAGE JE KATALOGPOSITION ---
    const toggleVorlage = (item: ServiceItem) => {
        if (vorlageOffenId === item.id) {
            setVorlageOffenId(null);
            return;
        }
        setVorlageOffenId(item.id);
        // Entwurf als KOPIE — der Katalogstand bleibt unberuehrt, bis
        // gespeichert wird.
        setVorlageEntwurf(kopiereVorlage(item.bestandteile) ?? null);
        setKontingentEntwurf(gueltigesKontingent(item.stundenKontingent));
        setKontingentEntwurfFehler(null);
        setVorlageFehlschlag(false);
    };

    // Editor meldet eine neue Liste. Faellt sie weg, faellt das Kontingent
    // mit (S1) — gespeichert wird dann ausdruecklich „keins".
    const aendereVorlageEntwurf = (neu: PositionsBestandteil[] | null) => {
        setVorlageEntwurf(neu);
        if (!neu || neu.length === 0) {
            setKontingentEntwurf(undefined);
            setKontingentEntwurfFehler(null);
        }
    };

    const handleSaveVorlage = async (index: number) => {
        // null heisst „keine Vorlage" — die jsonb-Spalte braucht das
        // ausdrueckliche null, sonst bliebe die alte Liste stehen.
        // Normalisiert gespeichert (Labels getrimmt); der Knopf ist bei
        // Fehlern ohnehin gesperrt, es faellt also nichts still heraus.
        const bestandteile = normalisiereBestandteile(vorlageEntwurf);
        const felder: Partial<ServiceItem> = { bestandteile };
        // Stundenkontingent (S1): nur mit Bestandteilen — wird die Vorlage
        // entfernt, geht es mit (handleUpdateServiceFields schreibt dann
        // ausdruecklich null). Mitgeschickt nur, wenn es sich AENDERT: so
        // bleibt „Bestandteile speichern" auch auf einer Datenbank ohne die
        // Spalte (Nr. 171 nicht gefahren) moeglich.
        const kontingent = kontingentFuerKopie({ bestandteile }, kontingentEntwurf).stundenKontingent;
        if (kontingent !== gueltigesKontingent(customCatalog[index]?.stundenKontingent)) felder.stundenKontingent = kontingent;
        const ok = await handleUpdateServiceFields(index, felder);
        setVorlageFehlschlag(!ok);
        if (ok) setVorlageOffenId(null);
    };

    const handleUpdateService = async (index: number, field: string, value: any) => {
      // Einheitenwechsel weg von 'Monat' raeumt eine getragene Pauschale mit
      // ab (Regel in flatRateSetup) — sonst deckte sie unsichtbar weiter.
      if (field === 'unit') {
          const patch = flatPatchForUnitChange(customCatalog[index] || {}, value);
          await handleUpdateServiceFields(index, { unit: value, ...(patch ?? {}) });
          return;
      }
      // Zuordnungswechsel weg von der Pauschale raeumt die getragene Deckung
      // mit ab (Regel in flatRateSetup) — sonst deckte die Position unter
      // ihrem neuen Etikett unsichtbar weiter.
      if (field === 'assignment') {
          const patch = flatPatchForAssignmentChange(customCatalog[index] || {}, value);
          await handleUpdateServiceFields(index, { assignment: value, ...(patch ?? {}) });
          return;
      }
      await handleUpdateServiceFields(index, { [field]: value } as Partial<ServiceItem>);
    };

    // Auswahl im Deckungs-Select einer Katalogzeile: assignment + flat_rate
    // als Paar schreiben (siehe flatRateSetup.applyFlatScopeChoice).
    const handleUpdateFlatScope = async (index: number, choice: FlatScopeChoice) => {
      const item = customCatalog[index];
      if (!item) return;
      await handleUpdateServiceFields(index, applyFlatScopeChoice(item, choice));
    };

    // --- TEMPLATE FUNCTIONS ---
    const handleCreateTemplate = () => {
        setCurrentTemplate({ name: '', items: [] });
        setIsEditingTemplate('new');
    };

    const handleEditTemplate = (tmpl: ServiceTemplate) => {
        setCurrentTemplate({ ...tmpl }); // Clone
        setIsEditingTemplate(tmpl.id);
    };

    const handleDeleteTemplate = async (id: string) => {
        if (!confirm("Vorlage unwiderruflich löschen?")) return;
        const { error } = await supabase.from('service_templates').delete().eq('id', id);
        if (!error) {
            fetchTemplates();
        } else {
            alert("Fehler beim Löschen: " + error.message);
        }
    };

    const handleSaveTemplate = async () => {
        if (!currentTemplate.name) return alert("Bitte Namen eingeben");
        onSaveStatusChange('saving');

        try {
            const payload = {
                name: currentTemplate.name,
                items: currentTemplate.items,
                updated_at: new Date().toISOString()
            };

            if (isEditingTemplate === 'new') {
                const { error } = await supabase.from('service_templates').insert([payload]);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('service_templates').update(payload).eq('id', isEditingTemplate);
                if (error) throw error;
            }

            await fetchTemplates();
            setIsEditingTemplate(null);
            onSaveStatusChange('saved');
        } catch (e: any) {
            console.error(e);
            alert("Fehler beim Speichern der Vorlage");
            onSaveStatusChange('error');
        }
    };

    const handleAddItemToTemplate = (masterItem: ServiceItem) => {
        // Bestandteile als eigene Kopie, nie dieselbe Liste wie im Katalog —
        // und das Stundenkontingent NUR mit ihnen (S1); ohne Vorlage fallen
        // beide Schluessel ganz weg.
        const newItem: ServiceItem = mitVorlagenKopie({
            ...masterItem,
            id: crypto.randomUUID(), // New ID for template instance
            quantityContracted: 1, // Default qty
            quantityUsed: 0,
            status: 'open' as const,
        });
        // Sort after adding
        const updatedList = [...(currentTemplate.items || []), newItem].sort((a,b) => (a.lvPosition||'').localeCompare(b.lvPosition||'', undefined, {numeric:true}));
        
        setCurrentTemplate(prev => ({
            ...prev,
            items: updatedList
        }));
    };

    const handleRemoveItemFromTemplate = (idx: number) => {
        const newItems = [...(currentTemplate.items || [])];
        newItems.splice(idx, 1);
        setCurrentTemplate(prev => ({ ...prev, items: newItems }));
    };

    const handleUpdateTemplateItem = (idx: number, field: keyof ServiceItem, value: any) => {
        const newItems = [...(currentTemplate.items || [])];
        newItems[idx] = { ...newItems[idx], [field]: value };
        setCurrentTemplate(prev => ({ ...prev, items: newItems }));
    };

    const assignmentOptions: {id: ServiceAssignment, label: string}[] = [
        { id: 'none', label: 'Keine / Sonstige' },
        { id: 'inspection', label: 'Begehung' },
        { id: 'vorank', label: 'Vorankündigung' },
        { id: 'unterlage', label: 'Unterlage (RAB)' },
        { id: 'sigeplan', label: 'SiGe-Plan' },
        { id: 'asplan', label: 'A+S-Plan' },
        { id: 'einweisung', label: 'Einweisung SiGe-Plan' },
        { id: 'site_rules', label: 'Baustellenordnung' },
        { id: 'planning_general', label: 'Planung Allg.' },
        { id: 'additional', label: 'Zusatz' },
        // SiFa: Stundenposition fuer „Zeit buchen" in der Gefaehrdungsbeurteilung
        // (docs/sifa-gbu-plan.md §10; optional, Entscheid O3).
        { id: 'gbu', label: 'Gefährdungsbeurteilung (SiFa)' },
        // Nur Anzeige: gesetzt wird 'flatrate' ausschliesslich ueber das
        // Deckungs-Select bei Einheit 'Monat' — direkt waehlbar entstuende
        // genau die halb angelegte Pauschale ohne Deckungsumfang.
        { id: 'flatrate', label: 'Monatspauschale' },
    ];

    const filteredMasterItems = customCatalog.filter(i =>
        (i.title || '').toLowerCase().includes(itemSearch.toLowerCase()) || 
        (i.lvPosition || '').toLowerCase().includes(itemSearch.toLowerCase())
    );

    return (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden h-full flex flex-col">
             <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex justify-between items-center shrink-0">
                <div>
                    <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2"><Database className="w-5 h-5 text-indigo-600"/> Leistungsverzeichnis & Vorlagen</h3>
                    <p className="text-sm text-slate-500 mt-1">Verwalten Sie den globalen Katalog und erstellen Sie Projekt-Vorlagen.</p>
                </div>
                {activeTab === 'master' && customCatalog.length === 0 && (<Button variant="secondary" onClick={handleSeedCatalog} className="bg-emerald-50 text-emerald-700 border-emerald-200" icon={<Database className="w-4 h-4"/>}>Standard-Katalog initialisieren</Button>)}
            </div>

            {/* TABS */}
            <div data-glass="leiste-in-karte" className="flex border-b border-slate-200 bg-white sticky top-0 z-20 shrink-0">
                <button onClick={() => setActiveTab('master')} className={`flex-1 py-3 text-sm font-bold text-center border-b-2 transition-colors ${activeTab === 'master' ? 'border-indigo-600 text-indigo-600 bg-indigo-50/20' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                    Stamm-Katalog ({customCatalog.length})
                </button>
                <button onClick={() => setActiveTab('templates')} className={`flex-1 py-3 text-sm font-bold text-center border-b-2 transition-colors ${activeTab === 'templates' ? 'border-indigo-600 text-indigo-600 bg-indigo-50/20' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
                    Vorlagen ({templates.length})
                </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
                
                {/* MASTER CATALOG TAB */}
                {activeTab === 'master' && (
                    <div className="space-y-6">
                        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                            <h4 className="text-xs font-bold text-slate-600 uppercase mb-3 flex items-center gap-2"><Plus className="w-4 h-4 text-blue-600"/> Neue Position anlegen</h4>
                            <div className="flex flex-wrap gap-2 items-end">
                                <div className="w-10 text-center flex flex-col items-center"><label className="text-[11px] uppercase font-bold text-slate-400 mb-1">Std.</label><input type="checkbox" checked={newService.isDefault || false} onChange={(e) => setNewService({...newService, isDefault: e.target.checked})} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 mb-2"/></div>
                                <div className="w-20"><label className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Pos. Nr.</label><input type="text" value={newService.lvPosition} onChange={(e) => setNewService({...newService, lvPosition: e.target.value})} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="1.1.10" /></div>
                                <div className="flex-1 min-w-[200px]"><label className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Bezeichnung</label><input type="text" value={newService.title} onChange={(e) => setNewService({...newService, title: e.target.value})} className="w-full px-2 py-1.5 border rounded text-sm" placeholder="Leistung..." /></div>
                                <div className="w-24"><label className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Preis (€)</label><input type="number" value={newService.unitPrice} onChange={(e) => setNewService({...newService, unitPrice: parseFloat(e.target.value) || 0})} className="w-full px-2 py-1.5 border rounded text-sm text-right" placeholder="0.00" /></div>
                                <div className="w-24"><label className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Einheit</label><select value={newService.unit} onChange={(e) => setNewService({...newService, unit: e.target.value as any, ...(flatPatchForUnitChange(newService, e.target.value) ?? {})})} className="w-full px-2 py-1.5 border rounded text-sm bg-white"><option value="Stück">Stück</option><option value="Stunden">Stunden</option><option value="Monat">Monat</option><option value="Pauschal">Pauschal</option></select></div>
                                <div className="w-28"><label className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Kategorie</label><select value={newService.category} onChange={(e) => setNewService({...newService, category: e.target.value as any})} className="w-full px-2 py-1.5 border rounded text-sm bg-white"><option value="Entwurf">Entwurf</option><option value="Ausführung">Ausführung</option><option value="Sonstiges">Sonstiges</option></select></div>
                                <div className="w-32"><label className="text-[11px] uppercase font-bold text-slate-400 block mb-1">Funktion</label><select value={newService.assignment} onChange={(e) => setNewService({...newService, assignment: e.target.value as any, ...(flatPatchForAssignmentChange(newService, e.target.value as any) ?? {})})} className="w-full px-2 py-1.5 border rounded text-sm bg-white text-blue-700 font-medium">{assignmentOptions.map(opt => <option key={opt.id} value={opt.id} disabled={opt.id === 'flatrate'}>{opt.label}</option>)}</select></div>
                                {/* MONATSPAUSCHALE: die Einheit 'Monat' allein erzeugt keine
                                    Deckung — erst die ausdrueckliche Auswahl hier macht die
                                    Position zur wirksamen Pauschale (flatRateSetup). */}
                                {isMonthlyUnit(newService.unit) && (
                                    <div className="w-40">
                                        <label className="text-[11px] uppercase font-bold text-amber-600 block mb-1">Pauschale deckt</label>
                                        <select
                                            value={flatScopeChoiceOf(newService)}
                                            onChange={(e) => setNewService({ ...newService, ...applyFlatScopeChoice(newService, e.target.value as FlatScopeChoice) })}
                                            className={`w-full px-2 py-1.5 border rounded text-sm font-medium ${flatScopeChoiceOf(newService) ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-white text-slate-500'}`}
                                        >
                                            <option value="">Keine Pauschale</option>
                                            {CATALOG_FLAT_SCOPES.map(scope => <option key={scope} value={scope}>{FLAT_SCOPE_LABELS[scope]}</option>)}
                                        </select>
                                    </div>
                                )}
                                <Button onClick={handleAddService} variant="primary" icon={<Plus className="w-4 h-4"/>}>Add</Button>
                            </div>
                            {/* PAKETPOSITION: optionale Bestandteil-Vorlage — wird beim
                                Anlegen eines Projekts, Nachtrags oder Angebots kopiert. */}
                            <div className="mt-3">
                                <button
                                    type="button"
                                    onClick={() => setNeuVorlageAuf(o => !o)}
                                    aria-expanded={neuVorlageAuf}
                                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-indigo-700"
                                >
                                    <ListChecks className="w-3.5 h-3.5" />
                                    Bestandteile (Vorlage){hatBestandteile(newService) ? ` · ${(newService.bestandteile || []).length}${neuKontingent !== undefined ? ` · ${formatStunden(neuKontingent)}` : ''}` : ' — optional'}
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${neuVorlageAuf ? 'rotate-180' : ''}`} />
                                </button>
                                {neuVorlageGeprueft && neuVorlagenFehler && (
                                    <p role="alert" className="mt-1 text-[11px] font-semibold text-red-700">
                                        Bestandteile: {neuVorlagenFehler}
                                    </p>
                                )}
                                {neuVorlageGeprueft && hatBestandteile(newService) && neuKontingentFehler && (
                                    <p role="alert" className="mt-1 text-[11px] font-semibold text-red-700">
                                        Stundenkontingent: {neuKontingentFehler}
                                    </p>
                                )}
                                {neuVorlageAuf && (
                                    <div className="mt-2 max-w-3xl space-y-3">
                                        <BestandteileEditor
                                            wert={newService.bestandteile}
                                            // Liste weg → Kontingent weg (S1).
                                            onChange={(neu) => {
                                                setNewService(prev => ({ ...prev, ...bestandteilePatch(neu) }));
                                                if (!neu) setNeuKontingentFehler(null);
                                            }}
                                            service={pruefPosition({ unit: newService.unit, assignment: newService.assignment, flatRate: newService.flatRate })}
                                        />
                                        {/* STUNDENKONTINGENT: nur an einer Paketposition (S1). */}
                                        {hatBestandteile(newService) && (
                                            <div className="max-w-xs">
                                                <StundenKontingentFeld
                                                    wert={newService.stundenKontingent}
                                                    onChange={(k) => setNewService(prev => ({ ...prev, stundenKontingent: k }))}
                                                    onFehler={setNeuKontingentFehler}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                            <table className="w-full text-left text-sm">
                                <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-xs border-b border-slate-100">
                                    <tr><th className="p-3 w-10 text-center" title="Standardmäßig ausgewählt?">Std.</th><th className="p-3 w-20">Pos.</th><th className="p-3">Bezeichnung</th><th className="p-3 w-24 text-right">Preis (€)</th><th className="p-3 w-24">Einheit</th><th className="p-3 w-28">Kategorie</th><th className="p-3 w-32">Funktion / Zuweisung</th><th className="p-3 w-10"></th></tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {customCatalog.map((item, idx) => {
                                        const vorlageAuf = !!item.id && vorlageOffenId === item.id;
                                        const anzahlVorlage = Array.isArray(item.bestandteile) ? item.bestandteile.length : 0;
                                        const kontingentVorlage = anzahlVorlage > 0 ? gueltigesKontingent(item.stundenKontingent) : undefined;
                                        const pruefKatalog = pruefPosition({ unit: item.unit, assignment: item.assignment, flatRate: item.flatRate });
                                        const entwurfFehler = vorlageAuf && vorlageEntwurf ? pruefeBestandteilListe(vorlageEntwurf, pruefKatalog) : null;
                                        return (
                                        <React.Fragment key={item.id || idx}>
                                        <tr className="hover:bg-slate-50 group">
                                            <td className="p-3 text-center"><input type="checkbox" checked={item.isDefault || false} onChange={(e) => handleUpdateService(idx, 'isDefault', e.target.checked)} className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"/></td>
                                            <td className="p-3 font-mono text-xs"><input type="text" value={item.lvPosition} onChange={(e) => handleUpdateService(idx, 'lvPosition', e.target.value)} className="bg-transparent w-full outline-none focus:text-blue-600" /></td>
                                            <td className="p-3">
                                                <input type="text" value={item.title} onChange={(e) => handleUpdateService(idx, 'title', e.target.value)} className="bg-transparent w-full outline-none font-medium focus:text-blue-600" />
                                                {item.id && (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleVorlage(item)}
                                                        aria-expanded={vorlageAuf}
                                                        className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${anzahlVorlage > 0 ? 'text-indigo-700 hover:text-indigo-900' : 'text-slate-400 hover:text-indigo-700'}`}
                                                        title="Bestandteile dieser Position (Vorlage für Projekte, Nachträge und Angebote)"
                                                    >
                                                        <ListChecks className="w-3.5 h-3.5" />
                                                        {anzahlVorlage > 0 ? `Bestandteile (${anzahlVorlage}${kontingentVorlage !== undefined ? ` · ${formatStunden(kontingentVorlage)}` : ''})` : 'Bestandteile'}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="p-3"><input type="number" value={item.unitPrice} onChange={(e) => handleUpdateService(idx, 'unitPrice', parseFloat(e.target.value) || 0)} className="bg-transparent w-full outline-none text-right font-mono text-slate-600 focus:text-blue-600" /></td>
                                            <td className="p-3 text-xs text-slate-500"><select value={item.unit} onChange={(e) => handleUpdateService(idx, 'unit', e.target.value)} className="bg-transparent outline-none focus:text-blue-600 cursor-pointer"><option value="Stück">Stück</option><option value="Stunden">Stunden</option><option value="Monat">Monat</option><option value="Pauschal">Pauschal</option></select></td>
                                            <td className="p-3 text-xs"><select value={item.category} onChange={(e) => handleUpdateService(idx, 'category', e.target.value)} className={`bg-transparent outline-none cursor-pointer px-2 py-0.5 rounded font-medium ${item.category === 'Entwurf' ? 'text-purple-700 bg-purple-50' : item.category === 'Ausführung' ? 'text-blue-700 bg-blue-50' : 'text-slate-700 bg-slate-100'}`}><option value="Entwurf">Entwurf</option><option value="Ausführung">Ausführung</option><option value="Sonstiges">Sonstiges</option></select></td>
                                            <td className="p-3 text-xs">
                                                <div className="flex items-center gap-2">
                                                    <select 
                                                      value={item.assignment || 'none'} 
                                                      onChange={(e) => handleUpdateService(idx, 'assignment', e.target.value)} 
                                                      className={`bg-transparent outline-none cursor-pointer px-2 py-0.5 rounded font-bold uppercase text-[11px] flex-1 ${item.assignment && item.assignment !== 'none' ? 'text-emerald-700 bg-emerald-50 border border-emerald-200' : 'text-slate-400 border border-transparent'}`}
                                                    >
                                                        {assignmentOptions.map(opt => <option key={opt.id} value={opt.id} disabled={opt.id === 'flatrate'}>{opt.label}</option>)}
                                                    </select>
                                                    
                                                    {/* Local Row Feedback */}
                                                    <div className="w-4 h-4 flex items-center justify-center">
                                                        {savingRowId === item.id && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
                                                        {savedRowId === item.id && <Check className="w-3 h-3 text-green-500" />}
                                                    </div>
                                                </div>
                                                {/* MONATSPAUSCHALE: Deckungsumfang auch am Bestand sichtbar
                                                    und aenderbar — ohne Auswahl bleibt die Position eine
                                                    gewoehnliche Monatsposition ohne Deckung. */}
                                                {(isMonthlyUnit(item.unit) || item.assignment === 'flatrate' || item.flatRate) && (
                                                    <div className="mt-1.5">
                                                        <select
                                                            value={flatScopeChoiceOf(item)}
                                                            onChange={(e) => handleUpdateFlatScope(idx, e.target.value as FlatScopeChoice)}
                                                            className={`w-full outline-none cursor-pointer px-2 py-0.5 rounded text-[11px] font-medium border ${flatScopeChoiceOf(item) ? 'text-amber-800 bg-amber-50 border-amber-200' : 'text-slate-400 bg-white border-slate-200'}`}
                                                            title="Deckungsumfang der Monatspauschale"
                                                        >
                                                            <option value="">Keine Pauschale</option>
                                                            {CATALOG_FLAT_SCOPES.map(scope => <option key={scope} value={scope}>{`Pauschale: ${FLAT_SCOPE_LABELS[scope]}`}</option>)}
                                                        </select>
                                                        {item.assignment === 'flatrate' && !flatScopeChoiceOf(item) && (
                                                            <div className="text-[10px] font-bold text-amber-700 mt-0.5 leading-tight">Deckungsumfang fehlt</div>
                                                        )}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3 text-right"><button onClick={() => handleRemoveService(idx)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4"/></button></td>
                                        </tr>
                                        {vorlageAuf && (
                                            <tr className="bg-slate-50/60">
                                                <td colSpan={8} className="px-3 pb-3 pt-1">
                                                    <div className="max-w-3xl space-y-2">
                                                        <BestandteileEditor
                                                            wert={vorlageEntwurf}
                                                            onChange={aendereVorlageEntwurf}
                                                            service={pruefKatalog}
                                                        />
                                                        {/* STUNDENKONTINGENT: Vorgabe fuer neue Positionen,
                                                            nur zusammen mit Bestandteilen (S1). */}
                                                        {Array.isArray(vorlageEntwurf) && vorlageEntwurf.length > 0 && (
                                                            <div className="max-w-xs">
                                                                <StundenKontingentFeld
                                                                    wert={kontingentEntwurf}
                                                                    onChange={setKontingentEntwurf}
                                                                    onFehler={setKontingentEntwurfFehler}
                                                                />
                                                            </div>
                                                        )}
                                                        <p className="text-[11px] text-slate-500">
                                                            Änderungen gelten nur für Positionen, die danach angelegt werden — laufende Projekte behalten ihre Kopie.
                                                        </p>
                                                        <div className="flex flex-wrap items-center gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="primary"
                                                                onClick={() => handleSaveVorlage(idx)}
                                                                disabled={!!entwurfFehler || !!kontingentEntwurfFehler || savingRowId === item.id}
                                                                icon={<Save className="w-3.5 h-3.5" />}
                                                            >
                                                                Bestandteile speichern
                                                            </Button>
                                                            <Button size="sm" variant="ghost" onClick={() => setVorlageOffenId(null)}>Verwerfen</Button>
                                                            {vorlageFehlschlag && (
                                                                <span className="text-[11px] font-semibold text-red-700">
                                                                    Speichern fehlgeschlagen — ist SQL Nr. 165 (Stundenkontingent: Nr. 171) schon gelaufen?
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* TEMPLATES TAB */}
                {activeTab === 'templates' && !isEditingTemplate && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div onClick={handleCreateTemplate} className="border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center p-8 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors group">
                                <div className="bg-slate-100 p-3 rounded-full mb-3 group-hover:bg-blue-200 group-hover:text-blue-700 transition-colors"><Plus className="w-6 h-6 text-slate-400" /></div>
                                <h3 className="font-bold text-slate-700">Neue Vorlage</h3>
                                <p className="text-xs text-slate-400 mt-1">Leeres Template erstellen</p>
                            </div>

                            {templates.map(tmpl => (
                                <div key={tmpl.id} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all group relative">
                                    <div className="flex justify-between items-start mb-2">
                                        <div className="bg-indigo-50 text-indigo-700 p-2 rounded-lg"><LayoutTemplate className="w-5 h-5"/></div>
                                        <div className="flex gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100 transition-opacity">
                                            <button onClick={() => handleEditTemplate(tmpl)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-blue-600"><Edit3 className="w-4 h-4"/></button>
                                            <button onClick={() => handleDeleteTemplate(tmpl.id)} className="p-1.5 hover:bg-slate-100 rounded text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-slate-800 text-lg mb-1">{tmpl.name}</h3>
                                    <p className="text-xs text-slate-500 mb-4 line-clamp-2">{tmpl.description || 'Keine Beschreibung'}</p>
                                    <div className="flex items-center gap-2 text-xs font-medium text-slate-600 bg-slate-50 px-2 py-1 rounded border border-slate-100 w-fit">
                                        <ListChecks className="w-3 h-3"/> {tmpl.items?.length || 0} Positionen
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* TEMPLATE EDITOR (SPLIT VIEW) */}
                {activeTab === 'templates' && isEditingTemplate && (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden animate-in slide-in-from-right-4 duration-300 flex flex-col h-full">
                        <div className="bg-white border-b border-slate-200 p-4 flex justify-between items-center shrink-0">
                            <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                                <LayoutTemplate className="w-5 h-5 text-indigo-600"/> 
                                {isEditingTemplate === 'new' ? 'Neue Vorlage erstellen' : 'Vorlage bearbeiten'}
                            </h3>
                            <div className="flex gap-2">
                                <Button variant="ghost" onClick={() => setIsEditingTemplate(null)}>Abbrechen</Button>
                                <Button variant="primary" onClick={handleSaveTemplate} icon={<Save className="w-4 h-4"/>}>Speichern</Button>
                            </div>
                        </div>
                        
                        <div className="p-6 flex-1 flex flex-col min-h-0">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 shrink-0">
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Vorlagen-Name</label>
                                    <input 
                                        type="text" 
                                        value={currentTemplate.name} 
                                        onChange={(e) => setCurrentTemplate({...currentTemplate, name: e.target.value})} 
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none"
                                        placeholder="z.B. Standard Wohnungsbau"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold text-slate-500 uppercase block mb-1">Beschreibung</label>
                                    <input 
                                        type="text" 
                                        value={currentTemplate.description || ''} 
                                        onChange={(e) => setCurrentTemplate({...currentTemplate, description: e.target.value})} 
                                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                        placeholder="Optionale Notiz..."
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1 min-h-0">
                                {/* Left: Template Content */}
                                <div className="flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm h-full">
                                    <div className="p-3 bg-indigo-50 border-b border-indigo-100 flex justify-between items-center shrink-0">
                                        <span className="text-xs font-bold text-indigo-900 uppercase tracking-wider flex items-center gap-2">
                                            <LayoutTemplate className="w-4 h-4"/> Vorlage Inhalt
                                        </span>
                                        <span className="text-xs font-bold bg-white px-2 py-0.5 rounded text-indigo-600 border border-indigo-200">
                                            {currentTemplate.items?.length || 0} Pos.
                                        </span>
                                    </div>
                                    <div className="flex-1 overflow-y-auto">
                                        {(!currentTemplate.items || currentTemplate.items.length === 0) ? (
                                            <div className="flex flex-col items-center justify-center h-full text-slate-400 p-6 text-center">
                                                <ListChecks className="w-12 h-12 mb-3 opacity-20" />
                                                <p className="text-sm font-medium">Vorlage ist leer.</p>
                                                <p className="text-xs mt-1">Fügen Sie Leistungen aus dem Katalog (rechts) hinzu.</p>
                                            </div>
                                        ) : (
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-slate-50 text-slate-400 text-[11px] font-bold uppercase sticky top-0 z-10">
                                                    <tr><th className="p-3 w-16">Pos.</th><th className="p-3">Titel</th><th className="p-3 w-20 text-center">Menge</th><th className="p-3 w-10"></th></tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {currentTemplate.items?.map((item, idx) => (
                                                        <tr key={idx} className="hover:bg-slate-50 group">
                                                            <td className="p-3 text-xs font-mono text-slate-500">{item.lvPosition}</td>
                                                            <td className="p-3 font-medium text-slate-700">{item.title}</td>
                                                            <td className="p-3 text-center">
                                                                <input 
                                                                    type="number" 
                                                                    value={item.quantityContracted} 
                                                                    onChange={(e) => handleUpdateTemplateItem(idx, 'quantityContracted', parseFloat(e.target.value) || 0)}
                                                                    className="w-16 text-center bg-transparent border border-transparent hover:border-slate-300 focus:border-indigo-500 rounded px-1 outline-none"
                                                                />
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <button onClick={() => handleRemoveItemFromTemplate(idx)} className="text-slate-300 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4"/></button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                </div>

                                {/* Right: Master Catalog Source */}
                                <div className="flex flex-col bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm h-full">
                                    <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-col gap-2 shrink-0">
                                        <div className="flex justify-between items-center">
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-2">
                                                <Database className="w-4 h-4"/> Stammkatalog
                                            </span>
                                        </div>
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                            <input 
                                                type="text" 
                                                placeholder="Leistung suchen..." 
                                                className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none"
                                                value={itemSearch}
                                                onChange={(e) => setItemSearch(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex-1 overflow-y-auto">
                                        {filteredMasterItems.length === 0 ? (
                                            <div className="p-8 text-center text-slate-400 italic text-sm">Keine Einträge gefunden.</div>
                                        ) : (
                                            <div className="divide-y divide-slate-100">
                                                {filteredMasterItems.map((item, idx) => {
                                                    const isAlreadyAdded = currentTemplate.items?.some(i => i.lvPosition === item.lvPosition);
                                                    
                                                    return (
                                                        <div 
                                                            key={idx} 
                                                            className={`flex items-center justify-between p-3 transition-colors ${
                                                                isAlreadyAdded 
                                                                ? 'bg-slate-50 opacity-60 cursor-not-allowed' 
                                                                : 'hover:bg-indigo-50 cursor-pointer group'
                                                            }`} 
                                                            onClick={() => !isAlreadyAdded && handleAddItemToTemplate(item)}
                                                        >
                                                            <div className="min-w-0 flex-1 pr-2">
                                                                <div className="flex items-center gap-2 mb-0.5">
                                                                    <span className="font-mono text-[11px] bg-slate-100 text-slate-500 px-1.5 rounded">{item.lvPosition}</span>
                                                                    <span className="text-xs text-slate-400">{item.category}</span>
                                                                </div>
                                                                <div className={`font-bold text-sm truncate ${isAlreadyAdded ? 'text-slate-500' : 'text-slate-700'}`}>{item.title}</div>
                                                            </div>
                                                            
                                                            {isAlreadyAdded ? (
                                                                <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-1 rounded border border-slate-200 flex items-center gap-1">
                                                                    <CheckCircle2 className="w-3 h-3" /> Drin
                                                                </span>
                                                            ) : (
                                                                <button className="bg-white border border-slate-200 text-indigo-600 p-1.5 rounded-lg hover:bg-indigo-600 hover:text-white transition-colors shadow-sm">
                                                                    <ArrowLeft className="w-4 h-4" />
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
         </div>
    );
};
